import imageCompression from 'browser-image-compression';

// Compression target chosen for community board context:
//   - longest edge 1600px (mobile-friendly, retina-acceptable)
//   - maxSizeMB 0.8 (most photos compress well below this)
//   - JPEG quality 0.82 default (browser-image-compression auto-tunes)
//   - WebWorker on to keep main thread responsive
//   - EXIF orientation auto-applied; GPS metadata stripped during canvas re-encode
const DEFAULT_OPTIONS = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  initialQuality: 0.82,
};

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const HARD_LIMIT_BYTES = 20 * 1024 * 1024;

// Compresses an image file. Returns the compressed File (with same name).
// Throws if the file is unsupported or absurdly large (>20MB pre-compression).
export async function compressImage(file, overrides = {}) {
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error('JPG, PNG, WebP 이미지만 업로드 가능합니다');
  }
  if (file.size > HARD_LIMIT_BYTES) {
    throw new Error('파일이 너무 큽니다 (최대 20MB)');
  }
  const compressed = await imageCompression(file, { ...DEFAULT_OPTIONS, ...overrides });
  // Preserve original filename for friendlier server-side keys (sub/{ulid}.{ext}
  // is generated server-side, but logs/readable bucket browsing helped by name).
  return new File([compressed], file.name, { type: compressed.type });
}

// Compresses a list of files in parallel. Returns { compressed: File[], errors: { file, error }[] }
export async function compressMany(files) {
  const results = await Promise.allSettled(
    Array.from(files).map(f => compressImage(f).then(c => ({ file: c })))
  );
  const compressed = [];
  const errors = [];
  results.forEach((r, i) => {
    if (r.status === 'fulfilled') compressed.push(r.value.file);
    else errors.push({ file: files[i], error: r.reason });
  });
  return { compressed, errors };
}
